import * as React from 'react';
import { AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

export type AlertKind = 'error' | 'warning' | 'info';

interface BannerStyle {
  bg: string;
  border: string;
  icon: typeof AlertCircle;
  iconColor: string;
  textColor: string;
}

// Per design spec (04-global-components.html §5 + README §4b).
// Tokens only — hex values used here are exactly those specified in the
// handoff for the non-tokenised semantic tints (red/amber/indigo banner skins).
const BANNER: Record<AlertKind, BannerStyle> = {
  error: {
    bg: '#FEE2E2',
    border: '#FECACA',
    icon: AlertCircle,
    iconColor: 'var(--status-expired)',
    textColor: '#7F1D1D',
  },
  warning: {
    bg: '#FEF3C7',
    border: '#FDE68A',
    icon: AlertTriangle,
    iconColor: '#D97706',
    textColor: '#78350F',
  },
  info: {
    bg: 'var(--accent-light)',
    border: 'var(--accent-muted)',
    icon: Info,
    iconColor: 'var(--accent)',
    textColor: '#1E1B4B',
  },
};

export interface AlertBannerProps {
  kind: AlertKind;
  children: React.ReactNode;
  action?: () => void;
  actionLabel?: string;
  onDismiss?: () => void;
}

export function AlertBanner({
  kind,
  children,
  action,
  actionLabel,
  onDismiss,
}: AlertBannerProps) {
  const b = BANNER[kind];
  const IconCmp = b.icon;
  return (
    <div
      role="alert"
      data-kind={kind}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '11px 16px',
        background: b.bg,
        border: `1px solid ${b.border}`,
        borderRadius: 'var(--radius-md)',
        fontSize: 13,
        color: b.textColor,
      }}
    >
      <IconCmp size={15} color={b.iconColor} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, lineHeight: 1.4 }}>{children}</span>
      {action && actionLabel && (
        <button
          type="button"
          onClick={action}
          style={{
            padding: '4px 12px',
            borderRadius: 6,
            border: 'none',
            background: b.iconColor,
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            fontFamily: 'inherit',
          }}
        >
          {actionLabel}
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 2,
            color: b.iconColor,
            flexShrink: 0,
            display: 'inline-flex',
          }}
        >
          <X size={13} />
        </button>
      )}
    </div>
  );
}

export default AlertBanner;
